import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import config from 'config'
import scriptLoader from 'react-async-script-loader'
import cx from 'classnames'
import { useForm } from 'react-hook-form'

import { required } from '@cred/shared/validations'

import { fireGtmEvent } from 'utils/gtm'
import LendersList from 'components/lenders-list'
import { STUDENT_LOAN_REFI_FILTER_FORM_INITIAL_VALUES as initialValues } from 'common/constants'
import StudentLoanRefiFilterForm from 'components/filter-form'
import { filterLender, loadLendersRates } from 'common/utils/rates'

import { minMaxLoanAmount } from '../validations'
import styles from './styles.module.scss'

const FullRateTableApp = ({
  widgetContext,
  theme,
  isScriptLoaded,
  ctaUrl,
  availableLenders = [],
}) => {
  const { watch, control, formState, reset } = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
  })
  const [loanAmount, creditScoreApprox] = watch(['loanAmount', 'creditScoreApprox'])

  useEffect(() => {
    fireGtmEvent('fieldChange', { loanAmount, ...widgetContext })
  }, [loanAmount])

  useEffect(() => {
    fireGtmEvent('fieldChange', { creditScore: parseInt(creditScoreApprox, 10), ...widgetContext })
  }, [creditScoreApprox])

  const lenders = useMemo(() => {
    if (isScriptLoaded) {
      return loadLendersRates({
        availableLenders: filterLender(availableLenders, creditScoreApprox, loanAmount),
        filters: { vertical: 'refi' },
      })
    }

    return []
  }, [isScriptLoaded, loanAmount, creditScoreApprox, availableLenders])

  const defaultTheme = theme.includes('default')

  const validateMinMaxLoanAmount = minMaxLoanAmount(availableLenders)

  return (
    <>
      <StudentLoanRefiFilterForm
        customValidation={[required, validateMinMaxLoanAmount]}
        className={cx([styles.filters], { [styles.default]: defaultTheme })}
        control={control}
        invalid={!formState.isValid}
      />
      <hr />
      <LendersList
        lenders={lenders}
        ctaUrl={ctaUrl}
        resetFilters={() => reset(initialValues)}
        theme={theme}
        name="StudentLoanRefi"
      />
    </>
  )
}

FullRateTableApp.propTypes = {
  isScriptLoaded: PropTypes.bool,
  ctaUrl: PropTypes.string.isRequired,
  widgetContext: PropTypes.object,
  theme: PropTypes.string,
  availableLenders: PropTypes.instanceOf(Array),
}

// Export named class and functions for unit tests
export { FullRateTableApp, initialValues }

export default scriptLoader(config.ratesLibraryUrl)(FullRateTableApp)
